/**
 * scaleScoringAdapter.js
 *
 * Bridges the modular Adult Autism form data model to the shared rating-scale
 * scorers in ScalesFunctions.js, and provides a single entry point that BOTH
 * modular save paths (Save & Continue and Save & Exit in GenericFormPage) call
 * to compute a persistable scaleResults blob.
 *
 * WHY THIS MODULE EXISTS SEPARATELY
 * ---------------------------------
 * The scorers (scorePHQ9, scoreASRS, scoreAQ50, …) were written for the ADHD
 * data model, where each answer lives *inside* the form definition tree at
 * `formSubsections[panel].questions[q].subquestions[sq].answer`. The modular
 * autism model instead keeps a flat `answers` map keyed by serviceUserVarName.
 *
 * Rather than fork a second set of scorers (which would create two sources of
 * truth for clinical numbers), we reconstruct the ADHD-shaped object on the fly
 * and reuse the exact same scoring logic. Extracting this into its own module —
 * separate from the large GenericFormPage component — keeps the pure, testable
 * logic isolated so it can be unit-tested without rendering the page, and lets
 * the two save paths share one implementation instead of duplicating it.
 */
import { scoreScale, isScorableScale } from './ScalesFunctions';

/**
 * Resolve the answer value for a subquestion from the flat answers map.
 * Supports both storage formats:
 *   - Flat (production): answers[serviceUserVarName]
 *   - Nested (legacy/test): answers[questionKey][serviceUserVarName]
 *
 * Also used by GenericFormPage's progress/summary helpers, so it lives here as
 * the single definition shared across the module.
 */
export function resolveAnswer(answers, questionKey, serviceUserVarName) {
  if (!answers || !serviceUserVarName) return undefined;
  // Flat format takes priority (production)
  if (answers[serviceUserVarName] !== undefined) return answers[serviceUserVarName];
  // Nested format fallback (legacy)
  if (answers[questionKey] && typeof answers[questionKey] === 'object') {
    return answers[questionKey][serviceUserVarName];
  }
  return undefined;
}

/**
 * Build the "formSubsections with embedded answers" shape that the shared
 * scale-scoring functions in ScalesFunctions.js expect.
 *
 * The scorers read each subquestion's stored answer from `subquestion.answer`,
 * so we clone the (static) form definition tree and inject the patient's flat
 * answers into it. Scale questions are plain selectOne items, so the stored
 * answer is the option label string the scorers key their scoreMaps on
 * (e.g. "Not at all").
 */
export function buildScoringFormData(formDefinition, answers) {
  const formSubsections = {};
  Object.entries(formDefinition?.formSubsections || {}).forEach(([subKey, subsection]) => {
    const questions = {};
    Object.entries(subsection?.questions || {}).forEach(([questionKey, question]) => {
      const subquestions = {};
      Object.entries(question?.subquestions || {}).forEach(([sqKey, subquestion]) => {
        subquestions[sqKey] = {
          ...subquestion,
          answer: resolveAnswer(answers, questionKey, subquestion.serviceUserVarName),
        };
      });
      questions[questionKey] = { ...question, subquestions };
    });
    formSubsections[subKey] = { ...subsection, questions };
  });
  return { formSubsections };
}

/**
 * Compute the scaleResults blob to persist for a modular form save, or null if
 * nothing should be written.
 *
 * This is the single scoring entry point shared by both GenericFormPage save
 * paths (Save & Continue and Save & Exit) so a patient who completes a scale via
 * either route ends up with an identical, fully-computed score. Scoring is
 * purely mechanical arithmetic on the recorded answers — no clinical inference.
 *
 * Returns null (i.e. "do not write a score") when:
 *   - the form has not reached 100% progress yet,
 *   - the form is not a scorable rating scale (e.g. a history form),
 *   - the scorer reports the scale is not fully complete (e.g. a question was
 *     skipped, which counts toward progress but leaves the scorer incomplete),
 *   - or the scorer throws. Scoring must NEVER block the patient from saving
 *     their answers, so any failure is swallowed here and logged.
 *
 * On success, returns the scorer result stamped with an ISO `lastUpdated` time
 * (matching the modular form instance's existing client-timestamp convention).
 *
 * @param {object} params
 * @param {string} params.formKey - The scale/form key (e.g. 'catq', 'phq9').
 * @param {object} params.formDefinition - The static form definition tree.
 * @param {object} params.answers - The flat answers map keyed by serviceUserVarName.
 * @param {number} params.progress - Recomputed 0-1 completion ratio for this save.
 * @returns {Promise<object|null>} The scaleResults blob to persist, or null.
 */
export async function computeScaleResultsForSave({ formKey, formDefinition, answers, progress }) {
  if (typeof progress !== 'number' || progress < 1) return null;
  if (!isScorableScale(formKey)) return null;

  try {
    const scoringFormData = buildScoringFormData(formDefinition, answers);
    const scaleResults = await scoreScale(formKey, scoringFormData);
    // Only persist a fully-computed result; partial/failed scoring is left
    // absent so the clinician panel simply shows the scale as "Completed"
    // (awaiting a score) rather than a stale or misleading number.
    if (!scaleResults || scaleResults.complete !== true) return null;
    return {
      ...scaleResults,
      lastUpdated: new Date().toISOString(),
    };
  } catch (scoreErr) {
    console.error(`[scaleScoringAdapter] Scale scoring failed for ${formKey}:`, scoreErr);
    return null;
  }
}
